import React, { useState } from 'react';
import { motion } from 'framer-motion';
import Section from '../ui/Section';
import Button from '../ui/Button';
import { Mail, Phone, MapPin, CheckCircle } from 'lucide-react';
import { trackContactFormSubmit, trackExternalLink } from '../../hooks/useGoogleAnalytics';
import { useCV } from '../../context/CVContext';

const Contact: React.FC = () => {
  const { isCVMode, phone } = useCV();
  const [formState, setFormState] = useState({
    name: '',
    email: '',
    message: '',
    submitted: false,
    errors: {
      name: '',
      email: '',
      message: ''
    }
  });

  const validateForm = () => {
    const errors = {
      name: '',
      email: '',
      message: ''
    };
    let isValid = true;

    if (!formState.name.trim()) {
      errors.name = 'Il nome è obbligatorio';
      isValid = false;
    }

    if (!formState.email.trim()) {
      errors.email = 'L\'email è obbligatoria';
      isValid = false;
    } else if (!/\S+@\S+\.\S+/.test(formState.email)) {
      errors.email = 'Formato email non valido';
      isValid = false;
    }

    if (!formState.message.trim()) {
      errors.message = 'Il messaggio è obbligatorio';
      isValid = false;
    }

    setFormState(prev => ({ ...prev, errors }));
    return isValid;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) return;

    setFormState(prev => ({ ...prev, errors: { name: '', email: '', message: '' } }));

    try {
      const res = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formState.name,
          email: formState.email,
          message: formState.message,
        }),
      });

      if (!res.ok) throw new Error('server error');

      trackContactFormSubmit();
      setFormState(prev => ({ ...prev, submitted: true }));
    } catch {
      setFormState(prev => ({
        ...prev,
        errors: {
          ...prev.errors,
          message: 'Errore durante l\'invio. Riprova più tardi.',
        },
      }));
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormState(prev => ({
      ...prev,
      [name]: value,
      errors: {
        ...prev.errors,
        [name]: '' // Clear the error when the user starts typing
      }
    }));
  };

  return (
    <Section
      id="contact"
      title="Contattami"
      subtitle="Hai un progetto interessante? Parliamone insieme."
      className="bg-gray-50 dark:bg-gray-900"
    >
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6 }}
        >
          <h3 className="text-2xl font-bold mb-6 text-gray-900 dark:text-gray-100">
            Informazioni di contatto
          </h3>
          
          <div className="space-y-6">
            <div className="flex items-start">
              <div className="mr-4 p-3 bg-primary-100 dark:bg-primary-900/30 rounded-lg text-primary-600 dark:text-primary-400">
                <Mail size={24} />
              </div>
              <div>
                <h4 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-1">Email</h4>
                <a
                  href="mailto:walter@walterdev.eu"
                  className="text-gray-700 dark:text-gray-300 hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
                  onClick={() => trackExternalLink('mailto:walter@walterdev.eu', 'Email Contact')}
                >
                  walter@walterdev.eu
                </a>
              </div>
            </div>
            
            <div className="flex items-start">
              <div className="mr-4 p-3 bg-primary-100 dark:bg-primary-900/30 rounded-lg text-primary-600 dark:text-primary-400">
                <Phone size={24} />
              </div>
              <div>
                <h4 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-1">Telefono</h4>
                {isCVMode && phone ? (
                  <a
                    href={`tel:${phone.replace(/\s/g, '')}`}
                    className="text-gray-700 dark:text-gray-300 hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
                    onClick={() => trackExternalLink(`tel:${phone.replace(/\s/g, '')}`, 'Phone Contact')}
                  >
                    {phone}
                  </a>
                ) : (
                  <div>
                    <p className="text-gray-700 dark:text-gray-300">Riservato</p>
                    <p className="text-xs text-gray-400 dark:text-gray-500 italic mt-1">solo da curriculum</p>
                  </div>
                )}
              </div>
            </div>
            
            <div className="flex items-start">
              <div className="mr-4 p-3 bg-primary-100 dark:bg-primary-900/30 rounded-lg text-primary-600 dark:text-primary-400">
                <MapPin size={24} />
              </div>
              <div>
                <h4 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-1">Ubicazione</h4>
                <p className="text-gray-700 dark:text-gray-300">
                  Provincia di Milano, Italia
                </p>
              </div>
            </div>
          </div>
          
          {/* <div className="mt-8 bg-white dark:bg-gray-800 p-6 rounded-lg shadow-sm">
            <h4 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">
              Orari di disponibilità
            </h4>
            <ul className="space-y-3">
              <li className="flex justify-between">
                <span className="text-gray-700 dark:text-gray-300">Lunedì - Venerdì:</span>
                <span className="font-medium text-gray-900 dark:text-gray-100">9:00 - 18:00</span>
              </li>
              <li className="flex justify-between">
                <span className="text-gray-700 dark:text-gray-300">Sabato:</span>
                <span className="font-medium text-gray-900 dark:text-gray-100">Su appuntamento</span>
              </li>
              <li className="flex justify-between">
                <span className="text-gray-700 dark:text-gray-300">Domenica:</span>
                <span className="font-medium text-gray-900 dark:text-gray-100">Chiuso</span>
              </li>
            </ul>
          </div> */}
        </motion.div>
        
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          {formState.submitted ? (
            <div className="bg-white dark:bg-gray-800 rounded-lg p-8 shadow-sm h-full flex flex-col items-center justify-center text-center">
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ duration: 0.5 }}
                className="w-16 h-16 mb-6 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center text-green-600 dark:text-green-400"
              >
                <CheckCircle size={32} />
              </motion.div>
              <h3 className="text-2xl font-bold mb-4 text-gray-900 dark:text-gray-100">
                Messaggio inviato!
              </h3>
              <p className="text-gray-700 dark:text-gray-300 mb-6">
                Grazie per avermi contattato. Risponderò al tuo messaggio il prima possibile.
              </p>
              <Button 
                onClick={() => setFormState(prev => ({ ...prev, submitted: false, name: '', email: '', message: '' }))}
                variant="outline"
              >
                Invia un altro messaggio
              </Button>
            </div>
          ) : (
            <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm">
              <h3 className="text-2xl font-bold mb-6 text-gray-900 dark:text-gray-100">
                Inviami un messaggio
              </h3>
              
              <form onSubmit={handleSubmit}>
                <div className="mb-4">
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Nome completo
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formState.name}
                    onChange={handleChange}
                    className={`w-full px-4 py-2 border rounded-md focus:ring focus:ring-primary-300 focus:border-primary-500 dark:bg-gray-700 dark:border-gray-600 dark:focus:ring-primary-600 dark:text-white ${
                      formState.errors.name ? 'border-red-500 dark:border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="Il tuo nome"
                  />
                  {formState.errors.name && (
                    <p className="mt-1 text-sm text-red-600 dark:text-red-400">{formState.errors.name}</p>
                  )}
                </div>
                
                <div className="mb-4">
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Indirizzo email
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formState.email}
                    onChange={handleChange}
                    className={`w-full px-4 py-2 border rounded-md focus:ring focus:ring-primary-300 focus:border-primary-500 dark:bg-gray-700 dark:border-gray-600 dark:focus:ring-primary-600 dark:text-white ${
                      formState.errors.email ? 'border-red-500 dark:border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="La tua email"
                  />
                  {formState.errors.email && (
                    <p className="mt-1 text-sm text-red-600 dark:text-red-400">{formState.errors.email}</p>
                  )}
                </div>
                
                <div className="mb-6">
                  <label htmlFor="message" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Messaggio
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formState.message}
                    onChange={handleChange}
                    rows={6}
                    className={`w-full px-4 py-2 border rounded-md focus:ring focus:ring-primary-300 focus:border-primary-500 dark:bg-gray-700 dark:border-gray-600 dark:focus:ring-primary-600 dark:text-white ${
                      formState.errors.message ? 'border-red-500 dark:border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="Descrivimi il tuo progetto o domanda..."
                  />
                  {formState.errors.message && (
                    <p className="mt-1 text-sm text-red-600 dark:text-red-400">{formState.errors.message}</p>
                  )}
                </div>
                
                <Button 
                  type="submit"
                  variant="primary"
                  className="w-full"
                >
                  Invia messaggio
                </Button>
              </form>
            </div>
          )}
        </motion.div>
      </div>

      {/* <div className="mt-8 text-center text-xs text-gray-500 dark:text-gray-400">
        <p>Ultima compilazione: {new Date(document.lastModified).toLocaleDateString('it-IT', {
          year: 'numeric',
          month: 'long',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        })}</p>
      </div> */}
    </Section>
  );
};

export default Contact;